/* -------------------------- Design imports start -------------------------- */
import { Autorenew, DeleteRounded, Download, Image as ImageIcon, PictureAsPdf, ViewInAr } from '@mui/icons-material'
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Tooltip } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import moment from 'moment'
import React, { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type UseQueryResult } from '@tanstack/react-query'
import JSZip from 'jszip'
import { toast } from 'react-toastify'

import LogTool from '../../../logger/logTools'
import { formatBytes, isPrivileged } from '../../../utils/functions'
import { type FileInfo } from '../../../utils/types'
import { type PaginatedResponse } from '../../../api'
import { type GenericFilterType, type PaginatedQueryParameters } from '../../../api/hooks/utils/types'
import SmartTable from '../../../components/SmartTable'
import { type SmartTableColumnType } from '../../../utils/types'
import InfoField from '../../../components/inputs/InfoField'
import { convertArticleFileToScs, fetchFileList, determineApiFromUrl } from '../api/fileApi'
import { useUserContext } from '../../../utils/context'
import { useWebsocket } from '../../../utils/WebsocketProvider'
import {
  useArticleFileSearch,
  useRequestFileSearch,
  useOfferFileSearch,
  useContractFileSearch,
  useBillFileSearch,
  useGuestRequestFileSearch,
  type ArticleFileSearchFilters,
  type RequestFileSearchFilters,
  type OfferFileSearchFilters,
  type ContractFileSearchFilters,
  type BillFileSearchFilters,
  type GuestRequestFileSearchFilters,
} from '../hooks/useFilesystemQueries'
import { HoopsCADViewer } from '../../../components/cad'
import PdfViewer from '../../PdfHandling/PdfViewer'
import ImageViewer from '../../../components/widgets/ImageViewer'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  files: FileInfo[]
  filesUrl?: string
  onDownload?: (file: FileInfo) => void
  onDelete?: (file: FileInfo) => Promise<void>
  onDeleteMultiple?: (files: FileInfo[]) => Promise<void>
  loading?: boolean
  // Indicates if these are article files displayed in a request context
  isArticleFilesInRequest?: boolean
  downloadOnly?: boolean
}

// Extend FileInfo to have a key property for SmartTable
type FileInfoWithKey = FileInfo & {
  key: string
  isConvertingRow?: boolean
  conversionSourceSelf?: string
  conversionArticleId?: string
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
/**
 * SmartTable-based component for displaying uploaded files with pagination, selection, and actions
 */
export default function UploadedFilesTable(props: Props): ReactElement {
  const {
    files,
    filesUrl,
    onDownload,
    onDelete,
    onDeleteMultiple,
    loading = false,
    isArticleFilesInRequest = false,
    downloadOnly = false,
  } = props
  const { t } = useTranslation()
  const log = new LogTool({ context: 'UploadedFilesTable', enable: true, logLevel: 'warn' })
  const { user } = useUserContext()
  const { websocketMessage } = useWebsocket()
  const queryClient = useQueryClient()

  const [selectedFiles, setSelectedFiles] = useState<FileInfoWithKey[]>([])
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set())
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set())
  const [convertingFiles, setConvertingFiles] = useState<Set<string>>(new Set())
  const [convertingFileEntries, setConvertingFileEntries] = useState<Map<string, FileInfoWithKey>>(new Map())
  const [selectedScsFile, setSelectedScsFile] = useState<FileInfo | null>(null)
  const [selectedPdfFile, setSelectedPdfFile] = useState<FileInfo | null>(null)
  const [selectedImageFile, setSelectedImageFile] = useState<FileInfo | null>(null)
  const [viewerInstanceId, setViewerInstanceId] = useState(0)

  const selectableFiles = useMemo(
    () => selectedFiles.filter(file => !file.isConvertingRow),
    [selectedFiles]
  )

  // Helper function to check if a file belongs to a Guest Request
  const fileBelongsToGuestRequest = (file: FileInfo): boolean => {
    return !!(file as any).guestRequest || !!(file as any).guest_request
  }

  // Check if delete should be disabled for Staff users viewing Guest Request files or Article files in Request context
  const apiInfo = useMemo(() => filesUrl ? determineApiFromUrl(filesUrl) : { type: 'unknown' as const }, [filesUrl])
  const isStaffUser = user && isPrivileged(user, 'STAFF')
  const isGuestRequest = apiInfo.type === 'guestRequest'
  // Also check if any of the files belong to a guest request (for cases without URL context)
  const hasGuestRequestFiles = useMemo(() => 
    files.some(file => fileBelongsToGuestRequest(file)), 
    [files]
  )
  // Check if these are article files in request context (explicitly marked via prop)
  const shouldDisableDelete = isStaffUser && (isGuestRequest || hasGuestRequestFiles || isArticleFilesInRequest)
  const allowDelete = !downloadOnly && Boolean(onDelete)
  const allowDeleteMultiple = !downloadOnly && Boolean(onDeleteMultiple)

  /* ------------------------ Callback functions start ------------------------ */
  const handleDownload = (file: FileInfo): void => {
    if (onDownload) {
      onDownload(file)
    } else {
      // Default download behavior
      log.info('downloadFile', file)
      const link = document.createElement('a')
      if (!link) throw new Error('Failed to create link element')
      if (!file.document) throw new Error("Failed to download file. The file doesn't have a document url.")
      link.href = file.document
      link.setAttribute('download', file.name ?? '')
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
  }

  const handleDelete = async (file: FileInfo): Promise<void> => {
    if (!onDelete) return
    if (!file.self) {
      log.error('Cannot delete file: missing self URL')
      return
    }

    setDeletingFiles(prev => new Set(prev).add(file.self as string))
    try {
      await onDelete(file)
      // Remove from selection if it was selected
      setSelectedFiles(prev => prev.filter(f => f.self !== file.self))
    } catch (error) {
      log.error('Error deleting file', error)
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev)
        newSet.delete(file.self as string)
        return newSet
      })
    }
  }

  const handleDeleteMultiple = async (): Promise<void> => {
    if (!onDeleteMultiple || selectableFiles.length === 0) return

    setDeletingFiles(prev => {
      const newSet = new Set(prev)
      selectableFiles.forEach(file => {
        if (file.self) {
          newSet.add(file.self)
        }
      })
      return newSet
    })

    try {
      await onDeleteMultiple(selectableFiles)
      setSelectedFiles([])
    } catch (error) {
      log.error('Error deleting multiple files', error)
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev)
        selectableFiles.forEach(file => {
          if (file.self) {
            newSet.delete(file.self)
          }
        })
        return newSet
      })
    }
  }

  const handleDownloadMultiple = async (): Promise<void> => {
    if (selectableFiles.length === 0) return

    // If only one file is selected, download it normally
    if (selectableFiles.length === 1) {
      handleDownload(selectableFiles[0])
      return
    }

    // For multiple files, create a ZIP archive
    setDownloadingFiles(prev => {
      const newSet = new Set(prev)
      selectableFiles.forEach(file => {
        if (file.self) {
          newSet.add(file.self)
        }
      })
      return newSet
    })

    try {
      const zip = new JSZip()
      const downloadPromises = selectableFiles.map(async (file) => {
        if (!file.document) {
          log.warn('File missing document URL, skipping', file.name)
          return
        }

        try {
          const response = await fetch(file.document)
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${file.name}`)
          }
          const blob = await response.blob()
          const fileName = file.name || `file-${file.self}`
          zip.file(fileName, blob)
        } catch (error) {
          log.error(`Error downloading file ${file.name}`, error)
          throw error
        }
      })

      await Promise.all(downloadPromises)

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
      
      // Download the ZIP file
      const link = document.createElement('a')
      link.href = URL.createObjectURL(zipBlob)
      link.download = `files-${new Date().toISOString().split('T')[0]}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)

      toast.success(t('common:feedback.success.filesDownloaded') || 'Files downloaded successfully')
    } catch (error) {
      log.error('Error downloading multiple files', error)
      toast.error(t('common:feedback.error.filesDownloadError') || 'Error downloading files')
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev)
        selectableFiles.forEach(file => {
          if (file.self) {
            newSet.delete(file.self)
          }
        })
        return newSet
      })
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Data preparation start ------------------------- */
  const getFilenameFromDocument = (documentUrl?: string | null): string | undefined => {
    if (!documentUrl) return undefined
    const trimmed = documentUrl.split('?')[0]
    const segments = trimmed.split('/')
    const lastSegment = segments[segments.length - 1]
    return lastSegment || undefined
  }

  const getFileExtension = (file: FileInfo): string | undefined => {
    const name = file.name || getFilenameFromDocument(file.document)
    if (!name) return undefined
    const parts = name.split('.')
    if (parts.length < 2) return undefined
    return parts[parts.length - 1].toLowerCase()
  }

  const isScsFile = (file: FileInfo): boolean => getFileExtension(file) === 'scs'

  const isPdfFile = (file: FileInfo): boolean => getFileExtension(file) === 'pdf'

  const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'])
  const isImageFile = (file: FileInfo): boolean => {
    const ext = getFileExtension(file)
    return ext ? IMAGE_EXTENSIONS.has(ext) : false
  }

  const extractIdFromUrl = (url?: string | null): string | undefined => {
    if (!url) return undefined
    const match = url.match(/\/(\d+)\/?$/)
    return match?.[1]
  }

  const getArticleIdFromFile = (file: FileInfo): string | undefined => {
    const article = (file as { article?: unknown }).article
    if (typeof article === 'string') return extractIdFromUrl(article)
    if (article && typeof article === 'object' && 'self' in article) {
      return extractIdFromUrl((article as { self?: string }).self)
    }
    return undefined
  }

  const buildConvertingRow = (file: FileInfoWithKey, conversionKey: string): FileInfoWithKey => {
    const baseName = file.name || getFilenameFromDocument(file.document) || t('common:content.label.fileName')
    const articleId = getArticleIdFromFile(file)
    return {
      ...file,
      key: `converting-${conversionKey}-${Date.now()}`,
      name: `${baseName} -> ${t('common:content.label.converting', { defaultValue: 'Converting to SCS...' })}`,
      isConvertingRow: true,
      conversionSourceSelf: file.self ?? undefined,
      conversionArticleId: articleId,
    }
  }

  const getFileNameWithoutExtension = (file: FileInfo): string | undefined => {
    const name = file.name || getFilenameFromDocument(file.document)
    if (!name) return undefined
    const lastDotIndex = name.lastIndexOf('.')
    if (lastDotIndex === -1) return name
    return name.substring(0, lastDotIndex)
  }

  const hasScsFileForSource = (sourceFile: FileInfo, allFiles: FileInfoWithKey[]): boolean => {
    const sourceBaseName = getFileNameWithoutExtension(sourceFile)
    if (!sourceBaseName) return false
    
    const sourceArticleId = getArticleIdFromFile(sourceFile)
    
    return allFiles.some(file => {
      if (file.isConvertingRow) return false
      if (!isScsFile(file)) return false
      
      const scsBaseName = getFileNameWithoutExtension(file)
      if (!scsBaseName) return false
      
      // Check if base names match (case-insensitive)
      if (scsBaseName.toLowerCase() !== sourceBaseName.toLowerCase()) return false
      
      // If both files belong to articles, check if they belong to the same article
      if (sourceArticleId) {
        const scsArticleId = getArticleIdFromFile(file)
        if (scsArticleId && scsArticleId !== sourceArticleId) return false
      }
      
      return true
    })
  }

  const isConvertibleToScs = (file: FileInfo): boolean => {
    const ext = getFileExtension(file)
    if (!ext || ext === 'scs') return false
    if (!file.self || !file.self.includes('/items/files/')) return false
    return true
  }

  const handleConvertToScs = async (file: FileInfoWithKey): Promise<void> => {
    if (!file.self || !isConvertibleToScs(file)) return
    const conversionKey = file.self ?? file.key
    setConvertingFileEntries(prev => {
      if (prev.has(conversionKey)) return prev
      const next = new Map(prev)
      next.set(conversionKey, buildConvertingRow(file, conversionKey))
      return next
    })
    setConvertingFiles(prev => new Set(prev).add(file.self as string))
    try {
      await convertArticleFileToScs(file.self)
      toast.success(t('common:feedback.success.convertStarted') || 'Conversion started')
    } catch (error) {
      log.error('Error starting SCS conversion', error)
      toast.error(t('common:feedback.error.conversionFailed') || 'Conversion failed')
      setConvertingFileEntries(prev => {
        const next = new Map(prev)
        next.delete(conversionKey)
        return next
      })
    } finally {
      setConvertingFiles(prev => {
        const next = new Set(prev)
        next.delete(file.self as string)
        return next
      })
    }
  }

  const formatDateTime = (value: unknown): string | undefined => {
    if (!value) return undefined
    const date = value instanceof Date ? value : new Date(String(value))
    if (Number.isNaN(date.getTime())) return undefined
    return moment(date).format('DD.MM.YYYY HH:mm')
  }

  const getLegacyField = (value: unknown, key: string): unknown => {
    if (value && typeof value === 'object' && key in value) {
      return (value as Record<string, unknown>)[key]
    }
    return undefined
  }

  const selectedFile = selectedFiles[0]
  const resolvedFileName = selectedFile
    ? selectedFile.name || getFilenameFromDocument(selectedFile.document)
    : undefined
  const createdAtValue = formatDateTime(
    selectedFile?.storedAt
      ?? getLegacyField(selectedFile, 'stored_at')
      ?? getLegacyField(selectedFile, 'created_at')
      ?? getLegacyField(selectedFile, 'createdAt')
  )
  const infoPanelContent = selectedFile ? (
    <Grid container spacing={2} sx={{ padding: '5px' }}>
      <Grid item xs={12}>
        <InfoField
          fullWidth
          label={t('common:content.label.fileName')}
          value={resolvedFileName}
        />
      </Grid>
      <Grid item xs={12}>
        <InfoField
          fullWidth
          label={t('common:content.label.size')}
          value={typeof selectedFile.size === 'number' ? formatBytes(selectedFile.size) : undefined}
        />
      </Grid>
      <Grid item xs={12}>
        <InfoField
          fullWidth
          label={t('common:content.label.createdAt')}
          value={createdAtValue}
        />
      </Grid>
    </Grid>
  ) : null

  // Convert files to FileInfoWithKey format for SmartTable
  const filesWithKeys = useMemo<FileInfoWithKey[]>(() => {
    return files.map((file, index) => ({
      ...file,
      key: file.self || file.name || `file-${index}`,
    }))
  }, [files])

  // Unique cache key for local (non-URL) file lists to avoid React Query collisions
  const localFilesKey = useMemo(() => {
    if (filesUrl) return undefined
    return files.map(file => file.self || file.name || '').join('|')
  }, [files, filesUrl])

  const convertingRows = useMemo<FileInfoWithKey[]>(
    () => Array.from(convertingFileEntries.values()),
    [convertingFileEntries]
  )

  const mergeConvertingRows = (
    data?: PaginatedResponse<FileInfoWithKey>
  ): PaginatedResponse<FileInfoWithKey> | undefined => {
    if (!data) return data
    if (convertingRows.length === 0) return data

    const results: FileInfoWithKey[] = []
    const usedConversionKeys = new Set<string>()
    const convertingBySource = new Map<string, FileInfoWithKey>()
    convertingRows.forEach(row => {
      if (row.conversionSourceSelf) {
        convertingBySource.set(row.conversionSourceSelf, row)
      }
    })

    data.results.forEach((file) => {
      results.push(file)
      if (file.self) {
        const convertingRow = convertingBySource.get(file.self)
        if (convertingRow) {
          results.push(convertingRow)
          usedConversionKeys.add(convertingRow.key)
        }
      }
    })

    convertingRows.forEach((row) => {
      if (!usedConversionKeys.has(row.key)) {
        results.push(row)
      }
    })

    return {
      ...data,
      count: data.count + convertingRows.length,
      results,
    }
  }

  // Create a query hook that uses search hooks when searching, otherwise uses fetchFileList
  const useFilesQuery = (
    parameters: PaginatedQueryParameters<FileInfoWithKey, GenericFilterType>
  ): UseQueryResult<PaginatedResponse<FileInfoWithKey>> => {
    const sanitizedFilters: GenericFilterType = {}
    if (parameters.filters) {
      Object.entries(parameters.filters).forEach(([key, value]) => {
        if (typeof value === 'string') {
          const trimmed = value.trim()
          if (trimmed !== '') {
            sanitizedFilters[key] = trimmed
          }
          return
        }
        if (value !== undefined && value !== null) {
          sanitizedFilters[key] = value
        }
      })
    }

    const hasFilters = Object.keys(sanitizedFilters).length > 0
    const hasQ = typeof sanitizedFilters.q === 'string' && sanitizedFilters.q.trim() !== ''
    // When filters exist but no search query, use '*' to match all in search API
    const effectiveFilters = hasFilters && !hasQ
      ? { ...sanitizedFilters, q: '*' }
      : sanitizedFilters

    const page = parameters.page ?? 1
    const pageSize = parameters.pageSize ?? 10
    const from = (page - 1) * pageSize
    const size = pageSize

    // Determine API type from URL if available
    const apiInfo = filesUrl ? determineApiFromUrl(filesUrl) : { type: 'unknown' as const }
    const shouldUseSearch = filesUrl && (hasQ || hasFilters) && apiInfo.type !== 'unknown'

    const parseNumericFilter = (value: unknown): number | undefined => {
      if (typeof value === 'number' && Number.isFinite(value)) return value
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (trimmed === '') return undefined
        const parsed = Number(trimmed.replace(',', '.'))
        return Number.isNaN(parsed) ? undefined : parsed
      }
      return undefined
    }

    // Helper function to map filters to search filters
    const mapToSearchFilters = (type: string): ArticleFileSearchFilters | RequestFileSearchFilters | OfferFileSearchFilters | ContractFileSearchFilters | BillFileSearchFilters | GuestRequestFileSearchFilters | null => {
      const fileNameValue = typeof effectiveFilters.file_name === 'string' ? effectiveFilters.file_name : typeof effectiveFilters.fileName === 'string' ? effectiveFilters.fileName : typeof effectiveFilters.name === 'string' ? effectiveFilters.name : undefined
      const fileSizeValue = parseNumericFilter(
        effectiveFilters.file_size ?? effectiveFilters.fileSize ?? effectiveFilters.size
      )
      const searchQuery = typeof effectiveFilters.q === 'string' ? effectiveFilters.q : '*'

      const baseFilters = {
        q: searchQuery,
        fileName: fileNameValue,
        fileSize: fileSizeValue,
        from,
        size,
      }

      switch (type) {
        case 'article':
          return {
            ...baseFilters,
            articleId: apiInfo.resourceId,
            storedAt: typeof effectiveFilters.stored_at === 'string' ? effectiveFilters.stored_at : undefined,
          } as ArticleFileSearchFilters
        case 'request':
          return {
            ...baseFilters,
            requestId: apiInfo.resourceId,
            requestCertificate: typeof effectiveFilters.request_certificate === 'string' ? effectiveFilters.request_certificate : undefined,
            storedAt: typeof effectiveFilters.stored_at === 'string' ? effectiveFilters.stored_at : undefined,
          } as RequestFileSearchFilters
        case 'offer':
          return {
            ...baseFilters,
            offerId: apiInfo.resourceId,
            requestCertificate: typeof effectiveFilters.request_certificate === 'string' ? effectiveFilters.request_certificate : undefined,
            storedAt: typeof effectiveFilters.stored_at === 'string' ? effectiveFilters.stored_at : undefined,
          } as OfferFileSearchFilters
        case 'contract':
          return {
            ...baseFilters,
            contractId: apiInfo.resourceId,
            requestCertificate: typeof effectiveFilters.request_certificate === 'string' ? effectiveFilters.request_certificate : undefined,
            storedAt: typeof effectiveFilters.stored_at === 'string' ? effectiveFilters.stored_at : undefined,
          } as ContractFileSearchFilters
        case 'bill':
          return {
            ...baseFilters,
            billId: apiInfo.resourceId,
            requestCertificate: typeof effectiveFilters.request_certificate === 'string' ? effectiveFilters.request_certificate : undefined,
            storedAt: typeof effectiveFilters.stored_at === 'string' ? effectiveFilters.stored_at : undefined,
          } as BillFileSearchFilters
        case 'guestRequest':
          return {
            ...baseFilters,
            guestRequestId: apiInfo.resourceId,
            fileType: typeof effectiveFilters.file_type === 'string' ? effectiveFilters.file_type : undefined,
            createdAt: typeof effectiveFilters.created_at === 'string' ? effectiveFilters.created_at : undefined,
          } as GuestRequestFileSearchFilters
        default:
          return null
      }
    }

    // Use search hooks when searching
    const searchFilters = shouldUseSearch ? mapToSearchFilters(apiInfo.type) : null

    // Create default filter objects to prevent null errors when hooks are disabled
    const defaultArticleFilters: ArticleFileSearchFilters = { q: '*', from, size }
    const defaultRequestFilters: RequestFileSearchFilters = { q: '*', from, size }
    const defaultOfferFilters: OfferFileSearchFilters = { q: '*', from, size }
    const defaultContractFilters: ContractFileSearchFilters = { q: '*', from, size }
    const defaultBillFilters: BillFileSearchFilters = { q: '*', from, size }
    const defaultGuestRequestFilters: GuestRequestFileSearchFilters = { q: '*', from, size }

    // Article file search
    const articleSearch = useArticleFileSearch(
      (searchFilters as ArticleFileSearchFilters) ?? defaultArticleFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'article' && searchFilters !== null) }
    )

    // Request file search
    const requestSearch = useRequestFileSearch(
      (searchFilters as RequestFileSearchFilters) ?? defaultRequestFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'request' && searchFilters !== null) }
    )

    // Offer file search
    const offerSearch = useOfferFileSearch(
      (searchFilters as OfferFileSearchFilters) ?? defaultOfferFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'offer' && searchFilters !== null) }
    )

    // Contract file search
    const contractSearch = useContractFileSearch(
      (searchFilters as ContractFileSearchFilters) ?? defaultContractFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'contract' && searchFilters !== null) }
    )

    // Bill file search
    const billSearch = useBillFileSearch(
      (searchFilters as BillFileSearchFilters) ?? defaultBillFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'bill' && searchFilters !== null) }
    )

    // Guest request file search
    const guestRequestSearch = useGuestRequestFileSearch(
      (searchFilters as GuestRequestFileSearchFilters) ?? defaultGuestRequestFilters,
      { enabled: Boolean(shouldUseSearch && apiInfo.type === 'guestRequest' && searchFilters !== null) }
    )

    // Get the active search result based on type
    const activeSearchResult = shouldUseSearch ? (
      apiInfo.type === 'article' ? articleSearch :
      apiInfo.type === 'request' ? requestSearch :
      apiInfo.type === 'offer' ? offerSearch :
      apiInfo.type === 'contract' ? contractSearch :
      apiInfo.type === 'bill' ? billSearch :
      apiInfo.type === 'guestRequest' ? guestRequestSearch :
      null
    ) : null

    // Fallback to regular fetchFileList when not using search hooks
    // Create initial data from filesWithKeys when filesUrl is not provided
    const initialData = useMemo<PaginatedResponse<FileInfoWithKey> | undefined>(() => {
      if (filesUrl || shouldUseSearch) return undefined
      if (filesWithKeys.length === 0) return undefined
      
      const startIndex = (page - 1) * pageSize
      const endIndex = startIndex + pageSize
      const paginatedFiles = filesWithKeys.slice(startIndex, endIndex)
      
      return {
        count: filesWithKeys.length,
        next: endIndex < filesWithKeys.length ? page + 1 : null,
        previous: page > 1 ? page - 1 : null,
        results: paginatedFiles,
      } as PaginatedResponse<FileInfoWithKey>
    }, [filesUrl, shouldUseSearch, filesWithKeys, page, pageSize])
    
    const regularQuery = useQuery({
      queryKey: [
        'uploadedFiles',
        filesUrl ?? localFilesKey ?? 'local',
        filesWithKeys.length,
        parameters.page,
        parameters.pageSize,
        effectiveFilters,
        parameters.sort,
      ],
      queryFn: async () => {
        if (filesUrl) {
          const response = await fetchFileList(filesUrl, {
            ...(parameters as PaginatedQueryParameters<FileInfo, GenericFilterType>),
            filters: effectiveFilters,
            page,
            pageSize,
          })
          const results = response.results.map((file, index) => ({
            ...file,
            key: file.self || file.name || `file-${index + (page - 1) * pageSize}`,
          }))
          return {
            ...response,
            results,
          }
        }

        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginatedFiles = filesWithKeys.slice(startIndex, endIndex)

        return {
          count: filesWithKeys.length,
          next: endIndex < filesWithKeys.length ? page + 1 : null,
          previous: page > 1 ? page - 1 : null,
          results: paginatedFiles,
        } as PaginatedResponse<FileInfoWithKey>
      },
      enabled: !shouldUseSearch,
      initialData: initialData,
      placeholderData: (previousData) => previousData ?? initialData,
    })

    const searchResponse = useMemo<PaginatedResponse<FileInfoWithKey> | undefined>(() => {
      if (!shouldUseSearch || !activeSearchResult?.data) return undefined
      const searchData = activeSearchResult
      const results = searchData.files.map((file, index) => ({
        ...file,
        key: file.self || file.name || `file-${index + from}`,
      }))
      return {
        count: searchData.total,
        next: null, // Search APIs use offset-based pagination, URLs are not provided
        previous: null,
        results,
      }
    }, [shouldUseSearch, activeSearchResult?.data, activeSearchResult?.files, activeSearchResult?.total, from])

    const searchResult = useMemo<UseQueryResult<PaginatedResponse<FileInfoWithKey>> | null>(() => {
      if (!activeSearchResult) return null
      // Create a properly typed query result by mapping the search result
      // to match the expected UseQueryResult<PaginatedResponse<FileInfoWithKey>> type
      // We need to cast through unknown first because the search result types don't
      // exactly match, but we're transforming the data to the correct format.
      // The refetch will trigger a re-render, which will cause searchResponse to be recomputed.
      return {
        ...activeSearchResult,
        data: searchResponse,
      } as unknown as UseQueryResult<PaginatedResponse<FileInfoWithKey>>
    }, [activeSearchResult, searchResponse])

    // Return search wrapper query if using search hooks, otherwise regular query
    const baseQuery = (shouldUseSearch && searchResult) ? searchResult : regularQuery
    const mergedData = useMemo(
      () => mergeConvertingRows(baseQuery.data),
      [baseQuery.data, convertingRows]
    )
    return {
      ...baseQuery,
      data: mergedData,
    } as UseQueryResult<PaginatedResponse<FileInfoWithKey>>
  }
  /* -------------------------- Data preparation end -------------------------- */

  useEffect(() => {
    if (websocketMessage?.type !== 'files_scs_ready') return
    const articleId = String(websocketMessage.data?.article_id ?? '')
    if (!articleId) return
    setConvertingFileEntries(prev => {
      if (prev.size === 0) return prev
      const next = new Map(prev)
      next.forEach((value, key) => {
        if (value.conversionArticleId === articleId) {
          next.delete(key)
        }
      })
      return next
    })
    void queryClient.invalidateQueries({ queryKey: ['uploadedFiles'] })
    void queryClient.invalidateQueries({ queryKey: ['article-file-search'] })
    void queryClient.invalidateQueries({ queryKey: ['request-file-search'] })
    void queryClient.invalidateQueries({ queryKey: ['offer-file-search'] })
    void queryClient.invalidateQueries({ queryKey: ['contract-file-search'] })
    void queryClient.invalidateQueries({ queryKey: ['bill-file-search'] })
    void queryClient.invalidateQueries({ queryKey: ['guest-request-file-search'] })
  }, [websocketMessage, queryClient])

  // Collect all available files for SCS checking (from props and query data)
  const allAvailableFiles = useMemo<FileInfoWithKey[]>(() => {
    const filesFromProps = filesWithKeys
    // Try to get files from query cache if available
    const queryData = queryClient.getQueryData<PaginatedResponse<FileInfoWithKey>>([
      'uploadedFiles',
      filesUrl ?? localFilesKey ?? 'local',
    ])
    const filesFromQuery = queryData?.results ?? []
    
    // Combine and deduplicate by self URL
    const fileMap = new Map<string, FileInfoWithKey>()
    filesFromProps.forEach(file => {
      if (file.self) {
        fileMap.set(file.self, file)
      }
    })
    filesFromQuery.forEach(file => {
      if (file.self) {
        fileMap.set(file.self, file)
      }
    })
    
    return Array.from(fileMap.values())
  }, [filesWithKeys, filesUrl, queryClient])

  /* ------------------------- Render constants start ------------------------- */
  const columns: SmartTableColumnType<FileInfoWithKey>[] = [
    {
      title: t('common:content.label.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text: string, record: FileInfoWithKey) => {
        if (record.isConvertingRow) {
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <span>{text || '-'}</span>
            </Box>
          )
        }
        const hasExistingScs = hasScsFileForSource(record, allAvailableFiles)
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <span>{text || '-'}</span>
            {!isScsFile(record) && isConvertibleToScs(record) && !hasExistingScs && (
              <Tooltip title={t('common:content.label.convertToScs', { defaultValue: 'Convert to SCS' })}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    void handleConvertToScs(record)
                  }}
                  disabled={record.self ? convertingFiles.has(record.self) : true}
                  aria-label={t('common:content.label.convertToScs', { defaultValue: 'Convert to SCS' })}
                >
                  <Autorenew fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isScsFile(record) && (
              <Tooltip title={t('common:content.label.show3dModel', { defaultValue: 'Show 3D model' })}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedScsFile(record)
                    setViewerInstanceId(prev => prev + 1)
                  }}
                  aria-label={t('common:content.label.show3dModel', { defaultValue: 'Show 3D model' })}
                >
                  <ViewInAr fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isPdfFile(record) && record.document && (
              <Tooltip title={t('common:content.label.showPdf', { defaultValue: 'Show PDF' })}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedPdfFile(record)
                  }}
                  aria-label={t('common:content.label.showPdf', { defaultValue: 'Show PDF' })}
                >
                  <PictureAsPdf fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isImageFile(record) && record.document && (
              <Tooltip title={t('common:content.label.showImage', { defaultValue: 'Show image' })}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedImageFile(record)
                  }}
                  aria-label={t('common:content.label.showImage', { defaultValue: 'Show image' })}
                >
                  <ImageIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )
      },
      filter: {
        inputType: 'text',
        groupBy: {
          key: 'fileInformation',
          label: t('files:content.label.fileInformation', { defaultValue: 'File Information' }),
        },
        filterKey: 'name',
      },
    },
    {
      title: t('common:content.label.size'),
      dataIndex: 'size',
      key: 'size',
      width: 120,
      render: (size: number, record: FileInfoWithKey) => (
        <span>{record.isConvertingRow ? '-' : (size ? formatBytes(size) : '-')}</span>
      ),
      filter: {
        inputType: 'number',
        groupBy: {
          key: 'fileInformation',
          label: t('files:content.label.fileInformation', { defaultValue: 'File Information' }),
        },
        filterKey: 'file_size',
      },
    },
    {
      title: t('common:content.label.createdAt'),
      dataIndex: 'storedAt',
      key: 'storedAt',
      width: 150,
      render: (storedAt: Date | undefined, record: FileInfoWithKey) => {
        if (record.isConvertingRow) return '-'
        if (!storedAt) return '-'
        const date = storedAt instanceof Date ? storedAt : new Date(storedAt)
        return <span>{moment(date).format('DD.MM.YYYY')}</span>
      },
      filter: {
        inputType: 'date',
        groupBy: {
          key: 'fileInformation',
          label: t('files:content.label.fileInformation', { defaultValue: 'File Information' }),
        },
        filterKey: 'stored_at',
      },
    },
  ]

  const contextMenuOptions = [
    {
      label: t('common:interaction.button.download'),
      icon: <Download />,
      onClick: (record: FileInfoWithKey) => {
        if (record.isConvertingRow) return
        handleDownload(record)
      },
    },
    ...(allowDelete && !shouldDisableDelete
      ? [
          {
            label: t('common:interaction.button.delete'),
            icon: <DeleteRounded />,
            onClick: (record: FileInfoWithKey) => {
              if (record.isConvertingRow) return
              void handleDelete(record)
            },
          },
        ]
      : []),
  ]

  const tableOptions = allowDeleteMultiple && !shouldDisableDelete && selectableFiles.length > 0
    ? [
        {
          label: t('common:interaction.button.deleteSelected', { count: selectableFiles.length }),
          onClick: () => {
            void handleDeleteMultiple()
          },
          id: 'deleteSelectedFiles',
        },
      ]
    : []

  // Create actions for SmartTable (displayed in the actions area)
  // Always show the buttons, but disable them when nothing is selected (like in itemsPage.tsx)
  const actions = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        variant="outlined"
        color="primary"
        startIcon={<Download />}
        onClick={() => void handleDownloadMultiple()}
        disabled={
          selectableFiles.length === 0 ||
          selectableFiles.some(file => 
            file.self && downloadingFiles.has(file.self)
          )
        }
        size="small"
      >
        {selectableFiles.length === 1
          ? t('common:interaction.button.download')
          : t('common:interaction.button.downloadSelected', { count: selectableFiles.length })}
      </Button>
      {allowDeleteMultiple && !shouldDisableDelete && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<DeleteRounded />}
          onClick={() => void handleDeleteMultiple()}
          disabled={
            selectableFiles.length === 0 ||
            selectableFiles.some(file => 
              file.self && deletingFiles.has(file.self)
            )
          }
          size="small"
        >
          {t('common:interaction.button.deleteSelected', { count: selectableFiles.length })}
        </Button>
      )}
    </Stack>
  )
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <>
      <SmartTable<FileInfoWithKey, GenericFilterType>
        tableId="uploadedFilesTable"
        columnsConfig={{
          allColumns: columns,
          initialVisibleColumnIndices: columns.map(col => col.dataIndex as string),
        }}
        queryConfig={{
          dataHook: useFilesQuery,
        }}
        selection={{
          state: selectedFiles,
          setState: setSelectedFiles,
        }}
        contextMenuOptions={contextMenuOptions}
        tableOptions={tableOptions}
        actions={actions}
        customInfoPanel={infoPanelContent}
        searchLabel={t('files:interaction.input.searchFiles', { defaultValue: t('common:content.label.search') })}
        hideFilters={downloadOnly}
        hideColumnSelector={downloadOnly}
      />
      <Dialog
        open={Boolean(selectedScsFile)}
        onClose={() => setSelectedScsFile(null)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {selectedScsFile?.name || t('common:content.label.fileName')}
        </DialogTitle>
        <DialogContent>
          <Box height="70vh" width="100%">
            {selectedScsFile && (
              <HoopsCADViewer
                file={selectedScsFile}
                showSidebar={true}
                showToolbar={true}
                containerId={`hoops-cad-viewer-modal-${viewerInstanceId}`}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(selectedPdfFile)}
        onClose={() => setSelectedPdfFile(null)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {selectedPdfFile?.name || t('common:content.label.fileName')}
        </DialogTitle>
        <DialogContent>
          <Box height="70vh" width="100%">
            {selectedPdfFile && (
              <PdfViewer file={selectedPdfFile} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(selectedImageFile)}
        onClose={() => setSelectedImageFile(null)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {selectedImageFile?.name || t('common:content.label.fileName')}
        </DialogTitle>
        <DialogContent>
          <Box height="70vh" width="100%">
            {selectedImageFile && (
              <ImageViewer file={selectedImageFile} />
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
